import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FadeIn } from '../components/animations';
import { FormSection, Input, Select, Textarea, Radio, Button } from '../components/ui/FormElements';
import { useAuth } from '../contexts/AuthContext';
import { ServiceFactory } from '../services/factory/ServiceFactory';

interface ImplementationFormData {
  // Section A: Client Information
  companyName: string;
  effectiveDate: string;
  sicCode: string;
  taxId: string;
  companyStructure: string;
  companyStructureOther?: string;
  headquartersState: string;
  mailingAddress: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;

  // Section B: Broker Information
  agencyName: string;
  agencyAddress: string;
  mainContactName: string;
  mainContactEmail: string;
  mainContactPhone: string;
  alternateContactName: string;
  alternateContactEmail: string;
  alternateContactPhone: string;

  // Section C: Eligibility Information
  employeeEligibilityRequirements: string;
  dependentEligibilityRequirements: string;
  domesticPartnersEligible: 'yes' | 'no';

  // Section D: Payroll Information
  firstPaydayDate: string;
  payScheduleVariesByClass: 'yes' | 'no';
  payScheduleVariation?: string;
  payPeriodsAnnually: string;
  deductionPeriodsAnnually: string;
  totalEmployeeCount: string;

  // Section E: Medical Plan Information
  waiverReasonsRequired: 'yes' | 'no';
  contributionVariesByClass: 'yes' | 'no';
  contributionVariation?: string;
  cobraInPlace: 'yes' | 'no';
  cobraAdministrator?: string;
  useProposalStrategy: 'yes' | 'no';
  proposalId?: string;
  adminFeePerEmployee: string;

  // Agreement
  employerSignatureName?: string;
  employerSignatureDate?: string;
  brokerSignatureName?: string;
  brokerSignatureDate?: string;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ImplementationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useQuery();
  const brokerId = query.get('brokerId') || '';
  const employerId = query.get('employerId') || '';
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [quoteData, setQuoteData] = useState<any>(null);
  const [formData, setFormData] = useState<ImplementationFormData>({
    // Initialize with empty values
    companyName: '',
    effectiveDate: '',
    sicCode: '',
    taxId: '',
    companyStructure: '',
    headquartersState: '',
    mailingAddress: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: '',
    agencyName: '',
    agencyAddress: '',
    mainContactName: '',
    mainContactEmail: '',
    mainContactPhone: '',
    alternateContactName: '',
    alternateContactEmail: '',
    alternateContactPhone: '',
    employeeEligibilityRequirements: '',
    dependentEligibilityRequirements: '',
    domesticPartnersEligible: 'no',
    firstPaydayDate: '',
    payScheduleVariesByClass: 'no',
    payPeriodsAnnually: '',
    deductionPeriodsAnnually: '',
    totalEmployeeCount: '',
    waiverReasonsRequired: 'no',
    contributionVariesByClass: 'no',
    cobraInPlace: 'no',
    useProposalStrategy: 'yes',
    adminFeePerEmployee: '',
  });

  const companyStructureOptions = [
    { value: '', label: 'Select company structure' },
    { value: 'llc', label: 'LLC' },
    { value: 'c_corp', label: 'C Corporation' },
    { value: 's_corp', label: 'S Corporation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
    { value: 'other', label: 'Other' },
  ];

  // Fetch quote data to pre-populate form
  useEffect(() => {
    const fetchQuoteData = async () => {
      try {
        setIsLoading(true);

        const apiService = ServiceFactory.createApiService();
        const data = await apiService.getQuoteById(id!, brokerId, employerId);

        if (data) {
          setQuoteData(data);

          // Pre-populate form with data
          setFormData(prev => ({
            ...prev,
            companyName: data.companyName || '',
            effectiveDate: data.ichraEffectiveDate || '',
            primaryContactEmail: data.brokerEmail || '',
            agencyName: data.agency || data.brokerName || '',
            mainContactName: data.brokerName || '',
            mainContactEmail: data.brokerEmail || '',
            proposalId: `PROP-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
          }));
        }
      } catch (err) {
        console.error('Error fetching quote data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchQuoteData();
    }
  }, [id, brokerId, employerId]);

  const handleInputChange = (field: keyof ImplementationFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const apiService = ServiceFactory.createApiService();

      const submissionData = {
        ...formData,
        submittedBy: user?.email,
        submittedAt: new Date().toISOString(),
      };

      // Use ServiceFactory for implementation submission
      const result = await apiService.submitImplementation(id!, submissionData);

      if (result) {
        alert('Implementation form submitted successfully!');
        navigate(`/quotes/${id}?brokerId=${brokerId}&employerId=${employerId}`);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);

    try {
      const apiService = ServiceFactory.createApiService();

      const draftData = {
        ...formData,
        isDraft: true,
        savedBy: user?.email,
        savedAt: new Date().toISOString(),
      };

      // Use ServiceFactory for draft saving
      const result = await apiService.saveImplementationDraft(id!, draftData);

      if (result) {
        alert('Draft saved successfully!');
      } else {
        throw new Error('Failed to save draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-seafoam border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-brand-gradient rounded-brand p-6 mb-8 text-white shadow-brand"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Implementation Form</h1>
            <p className="text-sky-100">ICHRA 2025 Implementation Guide</p>
            {quoteData && (
              <p className="text-sm text-sky-200 mt-2">
                Company: {quoteData.companyName} | Quote ID: {id}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/quotes/${id}?brokerId=${brokerId}&employerId=${employerId}`)}
            >
              Back to Quote
            </Button>
            <Button
              variant="secondary"
              onClick={handleSaveDraft}
              disabled={isSaving}
            >
              Save Draft
            </Button>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section A: Client Information */}
        <FadeIn>
          <FormSection
            title="Section A: Client Information"
            description="Company details and contact information"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Company Name"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                required
              />
              <Input
                label="Effective Date"
                type="date"
                value={formData.effectiveDate}
                onChange={(e) => handleInputChange('effectiveDate', e.target.value)}
                required
              />
              <Input
                label="SIC Code"
                value={formData.sicCode}
                onChange={(e) => handleInputChange('sicCode', e.target.value)}
              />
              <Input
                label="Tax ID #"
                value={formData.taxId}
                onChange={(e) => handleInputChange('taxId', e.target.value)}
                required
              />
              <Select
                label="Company Structure"
                value={formData.companyStructure}
                onChange={(e) => handleInputChange('companyStructure', e.target.value)}
                options={companyStructureOptions}
                required
              />
              {formData.companyStructure === 'other' && (
                <Input
                  label="Specify Other Structure"
                  value={formData.companyStructureOther || ''}
                  onChange={(e) => handleInputChange('companyStructureOther', e.target.value)}
                  required
                />
              )}
              <Input
                label="Company Headquarters State"
                value={formData.headquartersState}
                onChange={(e) => handleInputChange('headquartersState', e.target.value)}
                required
              />
              <div className="md:col-span-2">
                <Textarea
                  label="Mailing Address"
                  value={formData.mailingAddress}
                  onChange={(e) => handleInputChange('mailingAddress', e.target.value)}
                  rows={3}
                  required
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Primary Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Name"
                  value={formData.primaryContactName}
                  onChange={(e) => handleInputChange('primaryContactName', e.target.value)}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.primaryContactEmail}
                  onChange={(e) => handleInputChange('primaryContactEmail', e.target.value)}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData.primaryContactPhone}
                  onChange={(e) => handleInputChange('primaryContactPhone', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Billing Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Name"
                  value={formData.billingContactName}
                  onChange={(e) => handleInputChange('billingContactName', e.target.value)}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.billingContactEmail}
                  onChange={(e) => handleInputChange('billingContactEmail', e.target.value)}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData.billingContactPhone}
                  onChange={(e) => handleInputChange('billingContactPhone', e.target.value)}
                  required
                />
              </div>
            </div>
          </FormSection>
        </FadeIn>

        {/* Section B: Broker Information */}
        <FadeIn>
          <FormSection
            title="Section B: Broker Information"
            description="Agency and broker contact details"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Agency / Agent Name"
                value={formData.agencyName}
                onChange={(e) => handleInputChange('agencyName', e.target.value)}
                required
              />
              <div className="md:col-span-2">
                <Textarea
                  label="Mailing Address"
                  value={formData.agencyAddress}
                  onChange={(e) => handleInputChange('agencyAddress', e.target.value)}
                  rows={3}
                  required
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Main Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Name"
                  value={formData.mainContactName}
                  onChange={(e) => handleInputChange('mainContactName', e.target.value)}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.mainContactEmail}
                  onChange={(e) => handleInputChange('mainContactEmail', e.target.value)}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData.mainContactPhone}
                  onChange={(e) => handleInputChange('mainContactPhone', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Alternate Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Name"
                  value={formData.alternateContactName}
                  onChange={(e) => handleInputChange('alternateContactName', e.target.value)}
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.alternateContactEmail}
                  onChange={(e) => handleInputChange('alternateContactEmail', e.target.value)}
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData.alternateContactPhone}
                  onChange={(e) => handleInputChange('alternateContactPhone', e.target.value)}
                />
              </div>
            </div>
          </FormSection>
        </FadeIn>

        {/* Section C: Eligibility Information */}
        <FadeIn>
          <FormSection
            title="Section C: Eligibility Information"
            description="Employee and dependent eligibility requirements"
          >
            <div className="space-y-6">
              <Textarea
                label="What are the general eligibility requirements for employees to participate in your benefit plans?"
                placeholder="Ex: Full-time employees, after 60 days of employment"
                value={formData.employeeEligibilityRequirements}
                onChange={(e) => handleInputChange('employeeEligibilityRequirements', e.target.value)}
                rows={3}
                required
              />

              <Textarea
                label="What are the general eligibility requirements for dependents to be covered under your benefit plans?"
                value={formData.dependentEligibilityRequirements}
                onChange={(e) => handleInputChange('dependentEligibilityRequirements', e.target.value)}
                rows={3}
                required
              />

              <div>
                <label className="block text-base font-medium text-night dark:text-white mb-3">
                  Are domestic partners eligible for coverage under your benefit plan?
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Note: Domestic partners are only eligible for ICHRA if they qualify as the employee's IRS-defined tax dependent
                </p>
                <div className="flex gap-6">
                  <Radio
                    name="domesticPartners"
                    label="Yes"
                    value="yes"
                    checked={formData.domesticPartnersEligible === 'yes'}
                    onChange={() => handleInputChange('domesticPartnersEligible', 'yes')}
                  />
                  <Radio
                    name="domesticPartners"
                    label="No"
                    value="no"
                    checked={formData.domesticPartnersEligible === 'no'}
                    onChange={() => handleInputChange('domesticPartnersEligible', 'no')}
                  />
                </div>
              </div>
            </div>
          </FormSection>
        </FadeIn>

        {/* Section D: Payroll Information */}
        <FadeIn>
          <FormSection
            title="Section D: Payroll Information"
            description="Payroll schedule and employee information"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="What is the date of the first payday in the new plan year?"
                type="date"
                value={formData.firstPaydayDate}
                onChange={(e) => handleInputChange('firstPaydayDate', e.target.value)}
                required
              />

              <div>
                <label className="block text-base font-medium text-night dark:text-white mb-3">
                  Does this differ by the class of employee?
                </label>
                <div className="flex gap-6">
                  <Radio
                    name="payScheduleVaries"
                    label="Yes"
                    value="yes"
                    checked={formData.payScheduleVariesByClass === 'yes'}
                    onChange={() => handleInputChange('payScheduleVariesByClass', 'yes')}
                  />
                  <Radio
                    name="payScheduleVaries"
                    label="No"
                    value="no"
                    checked={formData.payScheduleVariesByClass === 'no'}
                    onChange={() => handleInputChange('payScheduleVariesByClass', 'no')}
                  />
                </div>
              </div>

              {formData.payScheduleVariesByClass === 'yes' && (
                <div className="md:col-span-2">
                  <Textarea
                    label="Please explain how the pay schedule varies"
                    value={formData.payScheduleVariation || ''}
                    onChange={(e) => handleInputChange('payScheduleVariation', e.target.value)}
                    rows={3}
                    required
                  />
                </div>
              )}

              <Input
                label="Number of pay periods annually"
                type="number"
                value={formData.payPeriodsAnnually}
                onChange={(e) => handleInputChange('payPeriodsAnnually', e.target.value)}
                required
              />

              <Input
                label="Number of deduction periods annually"
                type="number"
                value={formData.deductionPeriodsAnnually}
                onChange={(e) => handleInputChange('deductionPeriodsAnnually', e.target.value)}
                required
              />

              <Input
                label="How many employees currently work for your organization?"
                type="number"
                placeholder="Including full-time, part-time, and all other employment classifications"
                value={formData.totalEmployeeCount}
                onChange={(e) => handleInputChange('totalEmployeeCount', e.target.value)}
                required
              />
            </div>
          </FormSection>
        </FadeIn>

        {/* Section E: Medical Plan Information */}
        <FadeIn>
          <FormSection
            title="Section E: Medical Plan Information"
            description="Medical plan and contribution details"
          >
            <div className="space-y-6">
              <div>
                <label className="block text-base font-medium text-night dark:text-white mb-3">
                  Is the collection of waiver reasons required?
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Please note that we can only collect information for those enrolled through the one-on-one process
                </p>
                <div className="flex gap-6">
                  <Radio
                    name="waiverReasons"
                    label="Yes"
                    value="yes"
                    checked={formData.waiverReasonsRequired === 'yes'}
                    onChange={() => handleInputChange('waiverReasonsRequired', 'yes')}
                  />
                  <Radio
                    name="waiverReasons"
                    label="No"
                    value="no"
                    checked={formData.waiverReasonsRequired === 'no'}
                    onChange={() => handleInputChange('waiverReasonsRequired', 'no')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-base font-medium text-night dark:text-white mb-3">
                  Does the employee contribution vary by employee class?
                </label>
                <div className="flex gap-6">
                  <Radio
                    name="contributionVaries"
                    label="Yes"
                    value="yes"
                    checked={formData.contributionVariesByClass === 'yes'}
                    onChange={() => handleInputChange('contributionVariesByClass', 'yes')}
                  />
                  <Radio
                    name="contributionVaries"
                    label="No"
                    value="no"
                    checked={formData.contributionVariesByClass === 'no'}
                    onChange={() => handleInputChange('contributionVariesByClass', 'no')}
                  />
                </div>
              </div>

              {formData.contributionVariesByClass === 'yes' && (
                <Textarea
                  label="How does the contribution vary?"
                  value={formData.contributionVariation || ''}
                  onChange={(e) => handleInputChange('contributionVariation', e.target.value)}
                  rows={3}
                  required
                />
              )}

              <div>
                <label className="block text-base font-medium text-night dark:text-white mb-3">
                  Is COBRA Administration currently in place for the group?
                </label>
                <div className="flex gap-6">
                  <Radio
                    name="cobra"
                    label="Yes"
                    value="yes"
                    checked={formData.cobraInPlace === 'yes'}
                    onChange={() => handleInputChange('cobraInPlace', 'yes')}
                  />
                  <Radio
                    name="cobra"
                    label="No"
                    value="no"
                    checked={formData.cobraInPlace === 'no'}
                    onChange={() => handleInputChange('cobraInPlace', 'no')}
                  />
                </div>
              </div>

              {formData.cobraInPlace === 'yes' && (
                <Input
                  label="COBRA Administrator"
                  value={formData.cobraAdministrator || ''}
                  onChange={(e) => handleInputChange('cobraAdministrator', e.target.value)}
                  required
                />
              )}

              <div>
                <label className="block text-base font-medium text-night dark:text-white mb-3">
                  Would you like to proceed with the ICHRA contribution strategy outlined in your proposal?
                </label>
                <div className="flex gap-6">
                  <Radio
                    name="useProposal"
                    label="Yes"
                    value="yes"
                    checked={formData.useProposalStrategy === 'yes'}
                    onChange={() => handleInputChange('useProposalStrategy', 'yes')}
                  />
                  <Radio
                    name="useProposal"
                    label="No (please request an updated proposal with the desired strategy before finalizing)"
                    value="no"
                    checked={formData.useProposalStrategy === 'no'}
                    onChange={() => handleInputChange('useProposalStrategy', 'no')}
                  />
                </div>
              </div>

              {formData.useProposalStrategy === 'yes' && (
                <Input
                  label="Proposal ID"
                  value={formData.proposalId || ''}
                  onChange={(e) => handleInputChange('proposalId', e.target.value)}
                />
              )}

              <Input
                label="Total Administration Fee (per employee per month)"
                placeholder="Inclusive of administrative services, broker commissions, vendor fees, and related expenses"
                value={formData.adminFeePerEmployee}
                onChange={(e) => handleInputChange('adminFeePerEmployee', e.target.value)}
                required
              />
            </div>
          </FormSection>
        </FadeIn>

        {/* Acknowledgment and Agreement */}
        <FadeIn>
          <FormSection
            title="Acknowledgment and Agreement"
            description="Electronic signature and submission"
          >
            <div className="bg-gray-50 dark:bg-night-700 p-6 rounded-lg mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                By submitting this Implementation Form, the undersigned acknowledges and agrees to all terms and
                conditions outlined herein, including approval of the administrative fees as specified.
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                The undersigned further affirms that the accuracy and completeness of the information provided in this document are solely their
                responsibility. Advisory Health Administrators and Transperra Choice expressly disclaim any responsibility for the accuracy or completeness of the
                information contained in this document and shall not be held liable for any damages resulting from errors, omissions, or
                inaccuracies.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3">Employer Signature</h4>
                <Input
                  label="Print Name"
                  value={formData.employerSignatureName || ''}
                  onChange={(e) => handleInputChange('employerSignatureName', e.target.value)}
                />
                <Input
                  label="Date"
                  type="date"
                  value={formData.employerSignatureDate || ''}
                  onChange={(e) => handleInputChange('employerSignatureDate', e.target.value)}
                />
              </div>

              <div>
                <h4 className="font-semibold mb-3">Broker/Agent Signature</h4>
                <Input
                  label="Print Name"
                  value={formData.brokerSignatureName || ''}
                  onChange={(e) => handleInputChange('brokerSignatureName', e.target.value)}
                />
                <Input
                  label="Date"
                  type="date"
                  value={formData.brokerSignatureDate || ''}
                  onChange={(e) => handleInputChange('brokerSignatureDate', e.target.value)}
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/quotes/${id}?brokerId=${brokerId}&employerId=${employerId}`)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleSaveDraft}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSaving}
              >
                {isSaving ? 'Submitting...' : 'Submit Implementation Form'}
              </Button>
            </div>
          </FormSection>
        </FadeIn>
      </form>
    </div>
  );
};

export default ImplementationForm;